import React from 'react';
import { ProductCard } from '../components/ProductCard';
import InstallationAttentionModule from '../components/dashboard/InstallationAttentionModule';
import RiskOverviewModule from '../components/dashboard/RiskOverviewModule';
import { EmptyState } from '../components/status';
import { usePortalContext } from '../context/PortalContext';
import { MarketingGrid } from '../marketing/MarketingGrid';
import { useMarketing } from '../marketing/MarketingContext';
import { canDeployToAppCatalog, canManageClientAccounts, canUseCapaRegister, canUseRisk } from '../utils/capabilities';

const Dashboard = (): JSX.Element => {
  const { tenant, products } = usePortalContext();
  const { tiles, load } = useMarketing();
  const audit = products.find((p) => p.id === 'audit');
  const canManageClients = canManageClientAccounts(tenant);
  const canUseCapa = canUseCapaRegister(tenant);
  const canAccessRisk = canUseRisk(tenant);
  const canDeploy = canDeployToAppCatalog(tenant);

  const operateActions: Array<{ label: string; href: string; primary: boolean }> = [
    ...(tenant?.capabilities?.canUseSupplierCollaboration || tenant?.capabilities?.canManageSupplierCollaboration
      ? [{ label: 'Open collaboration', href: '/collaboration', primary: true }]
      : []),
    ...(canUseCapa
      ? [{ label: 'Open CAPA register', href: '/capa-register', primary: false }]
      : []),
    ...(canAccessRisk
      ? [{ label: 'Review risk', href: '/risk', primary: false }]
      : [])
  ];

  const administerLinkSummary = [
    canDeploy ? 'installations' : null,
    canManageClients ? 'client accounts' : null
  ].filter(Boolean).join(' and ');

  React.useEffect(() => {
    void load();
  }, [load]);

  const portalTiles = tiles.filter((tile) => tile.surfaces.portal);

  return (
    <div className="dashboard-page">
      <header className="dashboard-title">
        <h1 className="section-title">Dashboard</h1>
        <p className="muted">
          {tenant?.tenantName ? `${tenant.tenantName} · ` : ''}what needs attention, then the places you work.
        </p>
      </header>

      {canDeploy && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Needs attention</h2>
            <span className="section-sub">Installation health from the latest heartbeats and maintenance runs.</span>
          </div>
          <InstallationAttentionModule enabled={canDeploy} />
        </div>
      )}

      <div className="dashboard-section">
        <div className="section-header">
          <h2>Quick actions</h2>
          <span className="section-sub">Jump into the daily operational areas.</span>
        </div>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Daily work</h3>
            <p className="card-intro">Day-to-day collaboration and quality follow-up.</p>
            <div className="card-actions">
              {operateActions.length === 0 && <p>Your available products and support options are below.</p>}
              {operateActions.map((action) => (
                <a key={action.href} className={action.primary ? 'cta' : 'cta secondary'} href={action.href}>
                  {action.label}
                </a>
              ))}
            </div>
          </div>
          {(canDeploy || canManageClients) && (
            <div className="dashboard-card">
              <h3>Organisation administration</h3>
              <p className="card-intro">Manage {administerLinkSummary}.</p>
              <div className="card-actions">
                {canDeploy && <a className="cta secondary" href="/instances">View installations</a>}
                {canManageClients && <a className="cta secondary" href="/client-accounts">Open client accounts</a>}
              </div>
            </div>
          )}
          <div className="dashboard-card">
            <h3>Get help</h3>
            <p className="card-intro">Documentation and support channels.</p>
            <div className="card-actions">
              <a className="cta secondary" href="/docs">View docs</a>
              <a className="cta secondary" href="/support">Raise a ticket</a>
            </div>
          </div>
        </div>
      </div>

      {canAccessRisk && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Risk overview</h2>
            <span className="section-sub">Top items needing your attention right now.</span>
          </div>
          <RiskOverviewModule enabled={canAccessRisk} top={5} />
        </div>
      )}

      <div className="dashboard-section">
        <div className="section-header">
          <h2>Products you own</h2>
        </div>
        {products.length === 0 && (
          <EmptyState
            message={
              <>
                No products are currently assigned to this tenant. If this is unexpected, please{' '}
                <a href="/support">contact support</a>.
              </>
            }
          />
        )}
        {products.length > 0 && audit && <ProductCard product={audit} />}
        {products.length > 0 && !audit && (
          <EmptyState message="Products are entitled but not yet mapped to the portal catalogue." />
        )}
      </div>

      {portalTiles.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Marketing highlights</h2>
          </div>
          <MarketingGrid tiles={portalTiles} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;

